'use client';

import { Button, Card } from '@workspace/ui';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

interface ErrorProps {
  error: Error & { digest?: string };
}

export default function Error({ error }: ErrorProps) {
  const t = useTranslations('errors');

  const reset = () => {
    window.location.reload();
  };

  useEffect(() => {
    // Log the error to an error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      console.error('Application error:', error);
      // Here you would typically send the error to your error reporting service
      // Example: Sentry.captureException(error);
    }
  }, [error]);

  return (
    <div className="bg-grey-50 dark:bg-grey-1100 flex h-full items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 text-center">
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <h1 className="mb-2 text-2xl font-bold text-neutral-200 dark:text-neutral-100">
          {t('title')}
        </h1>

        <p className="mb-6 text-sm text-neutral-600 dark:text-neutral-400">{t('description')}</p>

        {process.env.ENVIRONMENT_NAME !== 'prod' && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Error Details (Development)
            </summary>
            <pre className="mt-2 rounded bg-neutral-100 p-3 text-xs text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={reset} variant="primary" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('tryAgain')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
